// @spec SHL-STR-040
'use client';

import { RefreshCw, WifiOff, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { cn } from '@/lib/utils';

export interface OfflineBannerProps {
  /** Optional: Callback when retry button is clicked */
  onRetry?: () => void;
  /** Optional: Custom message */
  message?: string;
  /** Optional: Additional CSS classes */
  className?: string;
}

/**
 * Banner component that displays when the user is offline.
 *
 * Features:
 * - Automatically shows/hides based on network status
 * - Retry button to manually check connection
 * - Toast notification when connection is restored
 * - Accessible with ARIA attributes
 * - Respects reduced motion preferences
 *
 * @example
 * // Add to root layout
 * <OfflineBanner />
 *
 * // With custom message
 * <OfflineBanner message="No network connection" />
 */
export function OfflineBanner({
  onRetry,
  message,
  className,
}: OfflineBannerProps) {
  const { t } = useTranslation('common');
  const displayMessage = message ?? t('offline.youreOffline');
  const { isOnline, checkConnection } = useOnlineStatus();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const wasOffline = useRef(false);

  // Track online/offline transitions for toast notification
  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
      setIsDismissed(false);
    } else if (wasOffline.current) {
      toast.success(t('offline.connectionRestored'), {
        description: t('offline.youreBackOnline'),
      });
      wasOffline.current = false;
    }
  }, [isOnline]);

  const handleRetry = useCallback(() => {
    setIsChecking(true);
    void checkConnection()
      .then((connected) => {
        if (connected) {
          toast.success(t('offline.connectionRestored'));
        } else {
          toast.error(t('offline.stillOffline'), {
            description: t('offline.checkConnection'),
          });
        }
        onRetry?.();
      })
      .finally(() => {
        setIsChecking(false);
      });
  }, [checkConnection, onRetry]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleDismiss();
      }
    },
    [handleDismiss]
  );

  // Don't render if online or dismissed
  if (isOnline || isDismissed) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      onKeyDown={handleKeyDown}
      className={cn(
        'fixed left-0 right-0 top-0 z-50',
        'animate-in slide-in-from-top duration-200',
        'motion-reduce:animate-none',
        className
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-4 px-4 py-3',
          'border-b border-warning bg-warning/10',
          'text-sm text-warning-foreground'
        )}
      >
        <div className="flex items-center gap-3">
          <WifiOff className="size-4 shrink-0 text-warning" aria-hidden="true" />
          <div>
            <p className="font-medium">{displayMessage}</p>
            <p className="text-muted-foreground">{t('offline.featuresUnavailable')}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRetry}
            disabled={isChecking}
            className="shrink-0"
          >
            <RefreshCw
              className={cn('size-3.5', isChecking && 'animate-spin')}
              aria-hidden="true"
            />
            <span>{isChecking ? t('offline.checking') : t('buttons.retry')}</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="size-8 shrink-0"
            aria-label={t('common:aria.dismissOfflineNotification')}
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
